// IndexedDB layer for ManuscriptSkim.
// Schema follows PRD §9.4. All v1 object stores are created here in Sprint 1, even
// though only `skim_reports` has a usable API yet — this prevents a v2 migration
// later when the other stores get CRUD APIs in Sprints 3–6.

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

export const DB_NAME = 'manuscript-skim';
export const DB_VERSION = 2;

export interface SkimReportRecord {
  id: string;
  manuscriptHash: string;
  manuscriptTitle: string;
  studyType: string | null;
  guidelineApplied: string | null;
  status: 'pattern_only' | 'llm_enriched' | 'editor_confirmed';
  methodologyScore: number | null;
  decisionRecommendation: string | null;
  /** Editor-authored prose, added in v2. Empty string for migrated v1 records. */
  decisionRationale: string;
  editorNotes: string;
  createdAt: number;
  updatedAt: number;
}

export interface SkimFindingRecord {
  id: string;
  skimReportId: string;
  category: 'methodology' | 'stats' | 'references' | 'voice' | 'scope' | 'language' | 'format';
  severity: 'info' | 'amber' | 'red';
  checklistItemId: string | null;
  findingText: string;
  evidencePassage: string;
  evidenceOffset: number;
  editorAction: 'confirmed' | 'dismissed' | 'edited' | null;
  layer: 'pattern' | 'llm' | 'api';
  modelVersion: string | null;
  createdAt: number;
}

export interface ReviewerCandidateRecord {
  id: string;
  skimReportId: string;
  name: string;
  affiliation: string | null;
  orcid: string | null;
  email: string | null;
  source: 'openalex' | 'pubmed' | 'crossref' | 'manual';
  relevantPaperDoi: string | null;
  selectedByEditor: boolean;
}

export interface AuditLogRecord {
  id: string;
  timestamp: number;
  eventType: string;
  skimReportId: string | null;
  payload: Record<string, unknown>;
  prevHash: string;
  thisHash: string;
}

export interface SettingsRecord {
  key: string;
  value: unknown;
}

interface MSDB extends DBSchema {
  settings: { key: string; value: SettingsRecord };
  skim_reports: {
    key: string;
    value: SkimReportRecord;
    indexes: { by_manuscript_hash: string };
  };
  skim_findings: {
    key: string;
    value: SkimFindingRecord;
    indexes: { by_skim_report: string };
  };
  reviewer_candidates: {
    key: string;
    value: ReviewerCandidateRecord;
    indexes: { by_skim_report: string };
  };
  audit_log: {
    key: string;
    value: AuditLogRecord;
    indexes: { by_timestamp: number };
  };
}

let dbPromise: Promise<IDBPDatabase<MSDB>> | null = null;

export function getDb(): Promise<IDBPDatabase<MSDB>> {
  if (!dbPromise) {
    dbPromise = openDB<MSDB>(DB_NAME, DB_VERSION, {
      async upgrade(db, oldVersion, _newVersion, tx) {
        if (oldVersion < 1) {
          db.createObjectStore('settings', { keyPath: 'key' });
          const reports = db.createObjectStore('skim_reports', { keyPath: 'id' });
          reports.createIndex('by_manuscript_hash', 'manuscriptHash', { unique: false });
          const findings = db.createObjectStore('skim_findings', { keyPath: 'id' });
          findings.createIndex('by_skim_report', 'skimReportId', { unique: false });
          const candidates = db.createObjectStore('reviewer_candidates', { keyPath: 'id' });
          candidates.createIndex('by_skim_report', 'skimReportId', { unique: false });
          const audit = db.createObjectStore('audit_log', { keyPath: 'id' });
          audit.createIndex('by_timestamp', 'timestamp', { unique: false });
        }
        if (oldVersion < 2) {
          // v2: SkimReportRecord gains decisionRationale (default '').
          const reports = tx.objectStore('skim_reports');
          let cursor = await reports.openCursor();
          while (cursor) {
            const existing = cursor.value as Partial<SkimReportRecord>;
            if (existing.decisionRationale === undefined) {
              await cursor.update({ ...(existing as SkimReportRecord), decisionRationale: '' });
            }
            cursor = await cursor.continue();
          }
        }
      },
    });
  }
  return dbPromise;
}

/** Test helper: reset the cached connection. Not exported through the barrel. */
export function _resetDbForTests(): void {
  dbPromise = null;
}
